import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const FreelancerSubscriptionHistoryPage = () => {
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const columns = [
    'subscription_history_id',
    'subscription_id',
    'freelancer_id',
    'freelancer_name',
    'subscribed_at_time',
    'subscription_plan_name',
    'subscription_validity_from',
    'subscription_validity_to',
    'payment_date',
    'subscription_fees_before_gst',
    'subscription_gst_amount',
    'subscription_fees_with_gst',
    'subscription_fees_payment_status',
    'invoice_number',
    'razorpay_payment_id',
    'razorpay_order_id',
    'razorpay_fee',
    'razorpay_fee_gst'
  ];

  const formatISTDateTime = (dateString, withTime = true) => {
    if (!dateString) return '-';
    const options = {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      ...(withTime && { hour: 'numeric', minute: '2-digit', hour12: true })
    };
    return new Date(dateString).toLocaleString('en-IN', options);
  };

  useEffect(() => {
    const fetchSubscriptionHistory = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = "/employee-login";
        return;
      }
      try {
        const response = await axios.get(`${apiBaseURL}/api/freelancer-subscription-info-history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const sorted = response.data.sort((a, b) => a.subscription_history_id - b.subscription_history_id); // ascending
// Or use: b.subscription_history_id - a.subscription_history_id for descending

setSubscriptionHistory(sorted);
setFilteredData(sorted);

      } catch (err) {
        setError('Failed to fetch subscription history.');
      } finally {
        setLoading(false);
      }
    };
    fetchSubscriptionHistory();
  }, [apiBaseURL]);

  useEffect(() => {
    let filtered = subscriptionHistory;

    if (searchQuery) {
      filtered = filtered.filter(info =>
        info.freelancer_id?.toString().includes(searchQuery) ||
        info.freelancer_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (planFilter) {
      filtered = filtered.filter(info =>
        info.subscription_plan_name?.toLowerCase().includes(planFilter.toLowerCase())
      );
    }

    if (fromDate && toDate) {
      const fromDateTime = new Date(fromDate);
      fromDateTime.setHours(0, 0, 0, 0);
      const toDateTime = new Date(toDate);
      toDateTime.setHours(23, 59, 59, 999);
      filtered = filtered.filter(info =>
        new Date(info.subscribed_at_time) >= fromDateTime &&
        new Date(info.subscribed_at_time) <= toDateTime
      );
    }

    setFilteredData(filtered);
  }, [searchQuery, planFilter, fromDate, toDate, subscriptionHistory]);

  const calculateTotalFeesBeforeGST = () =>
    filteredData.reduce((total, info) => total + Number(info.subscription_fees_before_gst || 0), 0);

  const calculateTotalFeesWithGST = () =>
    filteredData.reduce((total, info) => total + Number(info.subscription_fees_with_gst || 0), 0);

  const handleDownload = () => {
    const now = new Date();
    const formattedDate = now.toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0];
    const displayTimestamp = now.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

    const headingRows = [
      ['OrderMinds Freelancing Solutions LLP'],
      ['Freelancer Subscription History'],
      [`Downloaded on: ${displayTimestamp}`],
      [],
    ];

    const headers = columns.map(col => col.replace(/_/g, ' '));
    const rows = filteredData.map(row =>
      columns.map(col => row[col] !== undefined && row[col] !== null ? String(row[col]) : '')
    );

    const ws = XLSX.utils.aoa_to_sheet([...headingRows, headers, ...rows]);
    const maxColWidths = headers.map((_, colIndex) =>
      Math.max(...[...rows.map(row => String(row[colIndex] || '')).map(val => val.length), headers[colIndex].length])
    );
    ws['!cols'] = maxColWidths.map(w => ({ wch: w + 2 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Subscription History');
    const filename = `Freelancer_Subscription_History_${formattedDate}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const Table = ({ columns, data }) => (
    <div className="border rounded shadow">
      <table className="min-w-full table-auto bg-white">
        <thead className="sticky top-0 bg-gray-200 z-10">
          <tr>
            {columns.map((col, index) => (
              <th key={index} className="px-4 py-2 text-left capitalize font-medium text-sm text-gray-700 border-b">
                {col.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-2 text-center text-gray-500">
                No data available
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b hover:bg-gray-50">
                {columns.map((col, colIndex) => (
                  <td key={colIndex} className="px-4 py-2 text-sm text-gray-800 whitespace-nowrap">
                    {['subscribed_at_time', 'subscription_validity_from', 'subscription_validity_to', 'payment_date'].includes(col)
                      ? formatISTDateTime(row[col], true)
                      : row[col] !== undefined && row[col] !== null
                        ? String(row[col])
                        : '-'}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <div>Loading subscription history...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="flex-1 p-6 bg-gray-100 ml-64">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">Freelancer Subscription History</h2>
        <button
          onClick={handleDownload}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
        >
          Download Excel
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-4">
        <input
          type="text"
          className="border px-3 py-2 rounded"
          placeholder="Search Freelancer ID or Name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="border px-3 py-2 rounded"
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
        >
          <option value="">Select Plan</option>
          {[...new Set(subscriptionHistory.map(info => info.subscription_plan_name))].map(plan => (
            <option key={plan} value={plan}>{plan}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <label className="text-sm">From:</label>
          <input type="date" className="border px-3 py-2 rounded" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">To:</label>
          <input type="date" className="border px-3 py-2 rounded" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-4">
        <div className="bg-blue-100 border border-blue-400 text-blue-800 text-xl font-semibold px-6 py-3 rounded shadow-sm">
          📊 Total Subscriptions: {filteredData.length}
        </div>
        <div className="bg-green-100 border border-green-400 text-green-800 text-xl font-semibold px-6 py-3 rounded shadow-sm">
          💸 Total Subscription Fees Before GST: ₹{calculateTotalFeesBeforeGST()}
        </div>
        <div className="bg-green-100 border border-green-400 text-green-800 text-xl font-semibold px-6 py-3 rounded shadow-sm">
          💸 Total Subscription Fees With GST: ₹{calculateTotalFeesWithGST()}
        </div>
      </div>

      <Table columns={columns} data={filteredData} />
    </div>
  );
};

export default FreelancerSubscriptionHistoryPage;
